import { z } from "zod";
import { stamps, Money, Uuid } from "./common.js";
import { aiJobKind, aiJobStatus, canonicalType, confidence, productDomain } from "./enums.js";

// ---------------------------------------------------------------------------
// Structured-output schemas used with Claude (`output_config.format` /
// `messages.parse()`). Optional (non-required) properties are supported by
// structured outputs; numeric min/max constraints are stripped from the wire
// schema by the SDK helper and validated client-side.
// ---------------------------------------------------------------------------

/** One price row extracted from a PDF/pasted/ambiguous price list. */
export const ParsedPriceRow = z.object({
  supplierSku: z.string().optional(),
  manPartNumber: z.string().optional(),
  description: z.string(),
  listPrice: Money.optional(),
  discountPct: z.number().optional(),
  nettPrice: Money,
  vatCode: z.string().optional(),
  uom: z.string().optional(),
  packQty: z.number().int().optional(),
  termsCode: z.string().optional(),
});
export type ParsedPriceRow = z.infer<typeof ParsedPriceRow>;

/** Result envelope for the parse-price-list job. */
export const ParsedPriceRowsResult = z.object({
  rows: z.array(ParsedPriceRow),
});
export type ParsedPriceRowsResult = z.infer<typeof ParsedPriceRowsResult>;

/** One classified item in a batch classification call. */
export const ClassificationResultItem = z.object({
  /** Index of the item in the submitted batch. */
  index: z.number().int().nonnegative(),
  canonicalType: canonicalType,
  domain: productDomain,
  sizeMm: z.array(z.number()),
  packQty: z.number().int().optional(),
  uom: z.string().optional(),
  confidence: confidence,
});
export type ClassificationResultItem = z.infer<typeof ClassificationResultItem>;

/** Result envelope for the classify-products job (deterministic-first; LLM only for OTHER/unknown rows). */
export const ClassificationResult = z.object({
  items: z.array(ClassificationResultItem),
});
export type ClassificationResult = z.infer<typeof ClassificationResult>;

/**
 * Result of matching one buy-list line against a retrieved candidate
 * shortlist. `productId` must be one of the offered candidates, or null when
 * no candidate matches.
 */
/**
 * One requested item extracted from a buy-list document (invoice, order
 * confirmation, PO) by the AI parser.
 */
export const BuyListExtractLine = z.object({
  description: z.string(),
  qty: z.number().positive().optional(),
  uom: z.string().optional(),
  /**
   * Every labeled value printed on the source row, in column order, keys as
   * printed (e.g. PRODUCT CODE, DESCRIPTION, QTY, UNIT PRICE, TOTAL).
   */
  fields: z.array(z.object({ label: z.string(), value: z.string() })).nullish(),
});
export type BuyListExtractLine = z.infer<typeof BuyListExtractLine>;

export const BuyListExtractResult = z.object({
  lines: z.array(BuyListExtractLine),
  /** Document-level metadata the extractor spotted (all optional). */
  meta: z
    .object({
      /** e.g. "purchase order", "invoice", "order confirmation". */
      documentType: z.string().nullish(),
      /** PO / order / invoice number. */
      reference: z.string().nullish(),
      /** Date printed on the document (ISO where possible). */
      documentDate: z.string().nullish(),
      /** Issuing party (supplier / sender). */
      sender: z.string().nullish(),
    })
    .nullish(),
});
export type BuyListExtractResult = z.infer<typeof BuyListExtractResult>;

export const MatchResult = z.object({
  productId: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  rationale: z.string(),
});
export type MatchResult = z.infer<typeof MatchResult>;

// ---------------------------------------------------------------------------
// AI job log (`GET /api/ai-jobs`)
// ---------------------------------------------------------------------------

export const AiJobDto = z.object({
  id: Uuid,
  kind: aiJobKind,
  status: aiJobStatus,
  model: z.string(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  costUsd: Money.nullable(),
  /** Hash of the input; identical inputs are served from cache (zero cost). */
  inputHash: z.string().nullable(),
  error: z.string().nullable(),
  ...stamps,
});
export type AiJob = z.infer<typeof AiJobDto>;
